"use client";

import { useState, useEffect, useCallback } from "react";
import Modal from "@/app/components/common/Modal";
import {
  fetchFriends,
  fetchSharedInbox,
  markSharedSeen,
  createShareLink,
  createSessionShareLink,
} from "@/app/lib/api";
import { outboxShareSession } from "@/app/lib/outbox";
import { useAuth } from "@/app/lib/hooks/useAuth";
import type {
  FriendResponse,
  SharedInboxItem,
  ChatRecord,
  ProviderInfo,
  ShareLinkCreateRequest,
  ShareLinkProviderResponse,
} from "@/app/lib/types";

// Admin-only lifetime options for a public share link. The value is the
// pair of params sent to the server (24h=expiry_days:1, ..., Forever=
// permanent:true). Non-admin callers are clamped to 24h server-side.
interface ShareLifetimeOption {
  label: string;
  expiryDays: number;
  permanent: boolean;
}
const SHARE_LIFETIME_OPTIONS: ShareLifetimeOption[] = [
  { label: "24 hours", expiryDays: 1, permanent: false },
  { label: "3 days", expiryDays: 3, permanent: false },
  { label: "1 week", expiryDays: 7, permanent: false },
  { label: "1 month", expiryDays: 30, permanent: false },
  { label: "Forever", expiryDays: 1, permanent: true },
];

export interface OwnedSharedSession {
  session_id: string;
  title: string;
  participants: string[];
}

interface SharePanelProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  /// Sessions the current user owns AND has shared with at least one
  /// friend. Used to render the "Chats You've Shared" section so the
  /// user can revoke shares without having to navigate to each chat
  /// individually.
  ownedSharedSessions: OwnedSharedSession[];
  onShared: () => void;
  onSharedItemClick: (sessionId: string) => void;
  /// Called when the user taps "Manage" on one of their shared chats.
  /// Parent navigates to that session and opens the Manage Sharing modal.
  onManageSharing: (sessionId: string) => void;
  /// The most recent chat record of the active session, or null when the
  /// session has no records yet. Used ONLY for the temp-chat fallback,
  /// which assembles a single-turn public share link from the latest
  /// turn (a temp chat has no saved session id for the server to
  /// snapshot). Saved sessions share the whole session server-side and
  /// do not touch this.
  latestRecord?: ChatRecord | null;
  /// All providers — used to build the `providers_meta` (display_name +
  /// color) map for the single-turn temp-chat fallback link.
  providers: ProviderInfo[];
  /// True when the active chat is an unsaved temporary chat (no real
  /// session id). A temp chat cannot be snapshotted server-side, so the
  /// "Share a Link" path falls back to the single-turn `createShareLink`.
  isTempChat?: boolean;
}

export default function SharePanel({
  isOpen,
  onClose,
  sessionId,
  ownedSharedSessions,
  onShared,
  onSharedItemClick,
  onManageSharing,
  latestRecord,
  providers,
  isTempChat,
}: SharePanelProps) {
  const { isAdmin } = useAuth();
  const [friends, setFriends] = useState<FriendResponse[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  const [inboxItems, setInboxItems] = useState<SharedInboxItem[]>([]);
  const [loadingInbox, setLoadingInbox] = useState(false);

  // --- Public share link state ---
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  // Authoritative expiry from the server response (null = never expires).
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Index into SHARE_LIFETIME_OPTIONS — admin lifetime picker, default 24h.
  const [lifetimeIdx, setLifetimeIdx] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setShareError(null);
    setShareSuccess(false);
    setSelectedIds(new Set());
    // Reset the public-link section to its initial state each open.
    setLinkError(null);
    setLinkUrl(null);
    setLinkExpiresAt(null);
    setCopied(false);
    setCreatingLink(false);
    setLifetimeIdx(0);

    // Load friends only if there's an active session
    if (sessionId) {
      setLoadingFriends(true);
      fetchFriends()
        .then((data) => setFriends(data.friends))
        .catch((err) => console.error("Failed to load friends for share panel:", err))
        .finally(() => setLoadingFriends(false));
    }

    // Always load inbox
    setLoadingInbox(true);
    fetchSharedInbox()
      .then((data) => setInboxItems(data.items))
      .catch((err) => console.error("Failed to load shared inbox:", err))
      .finally(() => setLoadingInbox(false));
  }, [isOpen, sessionId]);

  const toggleFriend = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleShare = useCallback(async () => {
    if (!sessionId || selectedIds.size === 0) return;
    setShareError(null);
    setSharing(true);
    try {
      // outboxShareSession never throws — tries the API call and
      // queues for retry on transient failure. The optimistic UI
      // toast is shown immediately; if the call ends up queued,
      // recipients will see the share as soon as connectivity returns.
      await outboxShareSession(sessionId, { friend_ids: Array.from(selectedIds) });
      setShareSuccess(true);
      onShared();
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      // Should not be reached since outboxShareSession swallows errors,
      // but keep the surface for any unforeseen throw.
      setShareError(err instanceof Error ? err.message : "Failed to share");
    } finally {
      setSharing(false);
    }
  }, [sessionId, selectedIds, onShared, onClose]);

  // Create the public "Share a Link" link.
  //
  // For a normal saved session (a real session id): call
  // `createSessionShareLink`, which snapshots the WHOLE multi-turn
  // session server-side (every query, follow-up, provider output, and
  // judge run) — no per-record payload is assembled here.
  //
  // For a temp chat (no saved session id): fall back to the single-turn
  // `createShareLink`, assembling the ShareCreate body from the latest
  // record, mirroring iOS ShareService.createShare exactly: per-provider
  // model/output/duration_ms/mode/reasoning_effort/thinking_level/
  // max_output_tokens, plus providers_meta (display_name + color).
  const handleCreateLink = useCallback(async () => {
    setLinkError(null);
    setCopied(false);
    setCreatingLink(true);
    try {
      const lifetime = SHARE_LIFETIME_OPTIONS[lifetimeIdx];

      // Saved-session path — server snapshots the entire session.
      if (sessionId && !isTempChat) {
        const result = await createSessionShareLink({
          session_id: sessionId,
          permanent: lifetime.permanent,
          expiry_days: lifetime.expiryDays,
        });
        setLinkUrl(result.url);
        // The server is authoritative — display the returned expires_at,
        // never the requested lifetime (a non-admin always gets 24h).
        setLinkExpiresAt(result.expires_at);
        return;
      }

      // Temp-chat fallback — single-turn link from the latest record.
      if (!latestRecord) return;
      const providerMap = new Map(providers.map((p) => [p.id, p]));

      const responses: Record<string, ShareLinkProviderResponse> = {};
      for (const [pid, resp] of Object.entries(latestRecord.responses)) {
        const opts = (resp.options ?? {}) as Record<string, unknown>;
        const reasoningEffort =
          typeof opts.reasoning_effort === "string"
            ? opts.reasoning_effort
            : undefined;
        const thinkingLevel =
          typeof opts.thinking_level === "string"
            ? opts.thinking_level
            : undefined;
        const maxOutputTokens =
          typeof opts.max_output_tokens === "number"
            ? opts.max_output_tokens
            : undefined;
        responses[pid] = {
          model: resp.model,
          output: resp.text,
          duration_ms: resp.durationMs,
          mode: resp.mode,
          reasoning_effort: reasoningEffort,
          thinking_level: thinkingLevel,
          max_output_tokens: maxOutputTokens,
        };
      }

      const providersMeta: ShareLinkCreateRequest["providers_meta"] = {};
      for (const pid of latestRecord.providerOrder) {
        const provider = providerMap.get(pid);
        if (provider) {
          providersMeta[pid] = {
            display_name: provider.display_name,
            color: provider.color,
          };
        }
      }

      const body: ShareLinkCreateRequest = {
        prompt: latestRecord.prompt,
        mode: latestRecord.mode,
        provider_order: latestRecord.providerOrder,
        responses,
        providers_meta: providersMeta,
        permanent: lifetime.permanent,
        expiry_days: lifetime.expiryDays,
      };

      const result = await createShareLink(body);
      setLinkUrl(result.url);
      setLinkExpiresAt(result.expires_at);
    } catch (err) {
      setLinkError(
        err instanceof Error ? err.message : "Failed to create share link"
      );
    } finally {
      setCreatingLink(false);
    }
  }, [sessionId, isTempChat, latestRecord, providers, lifetimeIdx]);

  const handleCopyLink = useCallback(async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setLinkError("Could not copy to clipboard");
    }
  }, [linkUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!linkUrl) return;
    try {
      await navigator.share({ title: "Trio chat", url: linkUrl });
    } catch {
      // User dismissed the share sheet — not an error.
    }
  }, [linkUrl]);

  const handleInboxClick = useCallback(async (item: SharedInboxItem) => {
    try {
      if (item.unseen_count > 0) await markSharedSeen(item.session_id);
    } catch (err) {
      console.error("Failed to mark as seen:", err);
    }
    onSharedItemClick(item.session_id);
    onClose();
  }, [onSharedItemClick, onClose]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Human-readable expiry line for the public link. `null` (permanent
  // link) renders "Never expires".
  const formatExpiry = (dateStr: string | null): string => {
    if (!dateStr) return "Never expires";
    const d = new Date(dateStr);
    return `Expires ${d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  };

  const hasRecord = !!latestRecord;

  const sectionHeaderStyle = {
    fontSize: "11px",
    fontWeight: 600 as const,
    color: "var(--text-tertiary)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    padding: "12px 0 6px",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share">
      <div style={{ padding: "0 16px 16px", minWidth: "320px", maxHeight: "60vh", overflowY: "auto" }}>
        {/* Share with Friends — only when a session is active */}
        {sessionId && (
          <>
            <div style={sectionHeaderStyle}>Share with Friends</div>

            {shareError && (
              <div style={{
                padding: "8px 12px",
                marginBottom: "8px",
                borderRadius: "var(--radius)",
                background: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                fontSize: "13px",
              }}>
                {shareError}
              </div>
            )}

            {shareSuccess && (
              <div style={{
                padding: "8px 12px",
                marginBottom: "8px",
                borderRadius: "var(--radius)",
                background: "rgba(34, 197, 94, 0.15)",
                color: "#22c55e",
                fontSize: "13px",
              }}>
                Shared successfully!
              </div>
            )}

            {loadingFriends ? (
              <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
                Loading friends...
              </div>
            ) : friends.length === 0 ? (
              <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
                No friends to share with.
              </div>
            ) : (
              <>
                <div style={{ maxHeight: "200px", overflowY: "auto", marginBottom: "8px" }}>
                  {friends.map((friend) => (
                    <label
                      key={friend.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 4px",
                        cursor: "pointer",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(friend.id)}
                        onChange={() => toggleFriend(friend.id)}
                        style={{ accentColor: "var(--accent)", width: "16px", height: "16px" }}
                      />
                      <div>
                        <div style={{ fontSize: "14px", color: "var(--text-primary)" }}>
                          {friend.display_name || friend.email}
                        </div>
                        {friend.display_name && (
                          <div style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
                            {friend.email}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  className="btn-primary"
                  onClick={handleShare}
                  disabled={selectedIds.size === 0 || sharing || shareSuccess}
                  style={{ width: "100%", padding: "8px", fontSize: "13px", marginBottom: "4px" }}
                >
                  {sharing ? "Sharing..." : shareSuccess ? "Shared!" : `Share with ${selectedIds.size} friend${selectedIds.size !== 1 ? "s" : ""}`}
                </button>
              </>
            )}

            <div style={{ borderTop: "1px solid var(--border)", marginTop: "8px" }} />
          </>
        )}

        {/* Share a link — public link for the chat. A saved session
            shares the WHOLE multi-turn session; a temp chat (no saved
            session id) falls back to a single-turn link from the latest
            record. Render whenever there is something shareable. */}
        {(sessionId || (isTempChat && hasRecord)) && (
          <>
            <div style={sectionHeaderStyle}>Share a Link</div>

            <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "8px" }}>
              {isTempChat
                ? "Create a public link to the latest message in this chat. Anyone with the link can view it."
                : "Create a public link to this entire chat — every message, follow-up, and judge run. Anyone with the link can view it."}
            </div>

            {linkError && (
              <div style={{
                padding: "8px 12px",
                marginBottom: "8px",
                borderRadius: "var(--radius)",
                background: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                fontSize: "13px",
              }}>
                {linkError}
              </div>
            )}

            {!linkUrl ? (
              <>
                {/* Admin-only lifetime picker — non-admin links are
                    always clamped to 24h by the server. */}
                {isAdmin && (
                  <div style={{ marginBottom: "8px" }}>
                    <label
                      htmlFor="share-lifetime"
                      style={{
                        display: "block",
                        fontSize: "12px",
                        color: "var(--text-tertiary)",
                        marginBottom: "4px",
                      }}
                    >
                      Link expires after
                    </label>
                    <select
                      id="share-lifetime"
                      value={lifetimeIdx}
                      onChange={(e) => setLifetimeIdx(Number(e.target.value))}
                      disabled={creatingLink || !hasRecord}
                      style={{
                        width: "100%",
                        padding: "8px",
                        fontSize: "13px",
                        borderRadius: "var(--radius)",
                        border: "1px solid var(--border)",
                        background: "var(--surface)",
                        color: "var(--text-primary)",
                      }}
                    >
                      {SHARE_LIFETIME_OPTIONS.map((opt, idx) => (
                        <option key={opt.label} value={idx}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  className="btn-primary"
                  onClick={handleCreateLink}
                  disabled={creatingLink || !hasRecord}
                  style={{ width: "100%", padding: "8px", fontSize: "13px" }}
                >
                  {creatingLink
                    ? "Creating link..."
                    : isAdmin
                    ? "Create link"
                    : "Create share link"}
                </button>

                {!hasRecord && (
                  <div style={{
                    fontSize: "12px",
                    color: "var(--text-tertiary)",
                    marginTop: "6px",
                    textAlign: "center",
                  }}>
                    Send a message first to create a share link.
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  borderRadius: "var(--radius)",
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  marginBottom: "8px",
                }}>
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "13px",
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {linkUrl}
                  </div>
                  <button
                    className="btn-primary"
                    onClick={handleCopyLink}
                    style={{ fontSize: "12px", padding: "4px 12px", flexShrink: 0 }}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>

                <div style={{
                  fontSize: "12px",
                  color: "var(--text-tertiary)",
                  marginBottom: "8px",
                }}>
                  {formatExpiry(linkExpiresAt)}
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <a
                    className="btn-ghost"
                    href={`mailto:?subject=${encodeURIComponent(
                      "A Trio chat shared with you"
                    )}&body=${encodeURIComponent(
                      `Take a look at this Trio chat:\n\n${linkUrl}`
                    )}`}
                    style={{
                      flex: 1,
                      fontSize: "12px",
                      padding: "6px 10px",
                      textAlign: "center",
                      textDecoration: "none",
                    }}
                  >
                    Email
                  </a>
                  {typeof navigator !== "undefined" && "share" in navigator && (
                    <button
                      className="btn-ghost"
                      onClick={handleNativeShare}
                      style={{ flex: 1, fontSize: "12px", padding: "6px 10px" }}
                    >
                      Share…
                    </button>
                  )}
                </div>
              </>
            )}

            <div style={{ borderTop: "1px solid var(--border)", marginTop: "8px" }} />
          </>
        )}

        {/* Chats You've Shared — always visible when there are any */}
        {ownedSharedSessions.length > 0 && (
          <>
            <div style={sectionHeaderStyle}>Chats You&apos;ve Shared</div>
            {ownedSharedSessions.map((item) => (
              <div
                key={item.session_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "10px 4px",
                  borderBottom: "1px solid var(--border)",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {item.title || "Untitled"}
                  </div>
                  <div style={{
                    fontSize: "12px",
                    color: "var(--text-tertiary)",
                    marginTop: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    Shared with {item.participants.length === 1
                      ? item.participants[0]
                      : `${item.participants.length} people`}
                  </div>
                </div>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    onManageSharing(item.session_id);
                    onClose();
                  }}
                  style={{ fontSize: "12px", padding: "4px 10px", flexShrink: 0 }}
                >
                  Manage
                </button>
              </div>
            ))}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: "8px" }} />
          </>
        )}

        {/* Shared with You — always visible */}
        <div style={sectionHeaderStyle}>Shared with You</div>

        {loadingInbox ? (
          <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
            Loading...
          </div>
        ) : inboxItems.length === 0 ? (
          <div style={{ padding: "16px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
            No shared chats yet.
          </div>
        ) : (
          inboxItems.map((item) => (
            <button
              key={item.session_id}
              onClick={() => handleInboxClick(item)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "10px 4px",
                background: "none",
                border: "none",
                borderBottom: "1px solid var(--border)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: "14px",
                  color: "var(--text-primary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {item.title || "Untitled"}
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-tertiary)", marginTop: "2px" }}>
                  Shared by {item.shared_by} {item.last_activity_at ? `· ${formatDate(item.last_activity_at)}` : ""}
                </div>
              </div>
              {item.unseen_count > 0 && (
                <span style={{
                  marginLeft: "8px",
                  minWidth: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  background: "#ef4444",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "0 6px",
                  flexShrink: 0,
                }}>
                  {item.unseen_count}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
